import type { Theme } from '../lib/tokens';

// Warm shimmer bone — the skeleton primitive
export function Bone({ t, w, h = 14, r = 8, style = {} }: {
  t: Theme; w: number | string; h?: number | string; r?: number; style?: React.CSSProperties;
}) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: `linear-gradient(90deg, ${t.paperAlt} 25%, ${t.card} 50%, ${t.paperAlt} 75%)`,
      backgroundSize: '200% 100%',
      animation: 'pmShimmer 1.4s ease-in-out infinite',
      flexShrink: 0,
      ...style,
    }} />
  );
}

export function SkeletonDiscover({ t }: { t: Theme }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
      {/* Top bar */}
      <div style={{ padding: '6px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Bone t={t} w={110} h={24} r={6} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Bone t={t} w={38} h={38} r={100} />
          <Bone t={t} w={38} h={38} r={100} />
        </div>
      </div>
      {/* Location strip */}
      <div style={{ padding: '0 20px 14px', display: 'flex', gap: 8 }}>
        <Bone t={t} w={180} h={13} r={7} />
      </div>
      {/* Card */}
      <div style={{ flex: 1, position: 'relative', padding: '0 18px 12px' }}>
        <div style={{
          position: 'absolute', inset: '0 18px 12px', borderRadius: 24, overflow: 'hidden',
          background: t.card, border: `1px solid ${t.line}`,
        }}>
          <Bone t={t} w="100%" h="62%" r={0} />
          <div style={{ padding: '18px 18px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Bone t={t} w={130} h={26} r={8} />
              <Bone t={t} w={36} h={20} r={6} />
            </div>
            <Bone t={t} w={190} h={12} r={6} />
            <div style={{ display: 'flex', gap: 6 }}>
              <Bone t={t} w={78}  h={24} r={100} />
              <Bone t={t} w={90}  h={24} r={100} />
              <Bone t={t} w={70}  h={24} r={100} />
            </div>
            <Bone t={t} w="100%" h={54} r={14} />
          </div>
        </div>
      </div>
      {/* Action row */}
      <div style={{ padding: '2px 28px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Bone t={t} w={48} h={48} r={100} />
        <Bone t={t} w={62} h={62} r={100} />
        <Bone t={t} w={52} h={52} r={100} />
        <Bone t={t} w={62} h={62} r={100} />
        <Bone t={t} w={48} h={48} r={100} />
      </div>
    </div>
  );
}

export function SkeletonMatches({ t }: { t: Theme }) {
  return (
    <div style={{ flex: 1, overflow: 'hidden', background: t.paper, padding: '10px 20px' }}>
      <Bone t={t} w={140} h={30} r={8} style={{ marginBottom: 8 }} />
      <Bone t={t} w={170} h={12} r={6} style={{ marginBottom: 20 }} />
      {/* Avatar row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <Bone t={t} w={72} h={90} r={14} />
            <Bone t={t} w={48} h={11} r={5} />
          </div>
        ))}
      </div>
      <Bone t={t} w={110} h={11} r={5} style={{ marginBottom: 16 }} />
      {/* Match rows */}
      {[0, 1, 2].map(i => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${t.line}` }}>
          <Bone t={t} w={90} h={110} r={14} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
            <Bone t={t} w="55%" h={18} r={6} />
            <Bone t={t} w="80%" h={12} r={5} />
            <div style={{ display: 'flex', gap: 6 }}>
              <Bone t={t} w={80} h={22} r={100} />
              <Bone t={t} w={60} h={22} r={100} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
